import dayjs from "dayjs";
import {
  AccountNotifications,
  DownloadNotification,
  Notification,
} from "./useNotifications";
import { NOTIFICATIONS_TYPE_TO_DELETE } from "../../../../../../../../utils/constants";
import LocalStorage from "../../../../../../../../utils/LocalStorage";

export const getNotifications = (accountId: string): AccountNotifications =>
  (LocalStorage.getJSONValue("notifications")[
    accountId
  ] as AccountNotifications) || {
    notifications: [],
    areNewNotificationsAvailable: false,
  };

export const filterNotificationsToDelete = (
  notifications: AccountNotifications["notifications"],
  notificationsExpiryDate: number
): [
  validNotifications: AccountNotifications["notifications"],
  notificationsToDelete: AccountNotifications["notifications"]
] =>
  notifications.reduce(
    ([valid, toDelete], notification) => {
      const isExpired =
        notification.isExpired ||
        dayjs(notification.createdAt).isBefore(
          dayjs().subtract(notificationsExpiryDate, "days")
        );

      const isTypeToDelete = NOTIFICATIONS_TYPE_TO_DELETE.includes(
        notification.payload.type ?? ""
      );

      if (isExpired && isTypeToDelete) {
        return [valid, [...toDelete, notification]];
      }
      return [[...valid, notification], toDelete];
    },
    [[], []] as [
      AccountNotifications["notifications"],
      AccountNotifications["notifications"]
    ]
  );

export const savePromotionDownloadNotification = (
  accountId: string,
  payload: DownloadNotification,
  isMenuOpen: boolean
): AccountNotifications => {
  const newNotification: Notification = {
    createdAt: new Date(Date.now()),
    isRead: isMenuOpen,
    isExpired: false,
    payload,
  };
  const existingNotifications =
    LocalStorage.getJSONValue("notifications") || {};
  const existingNotificationsForCurrentAccount = (existingNotifications[
    accountId
  ] as AccountNotifications) || {
    notifications: [],
    areNewNotificationsAvailable: false,
  };

  const isNotificationAlreadySaved =
    existingNotificationsForCurrentAccount.notifications.some(
      (notification) => notification.payload.id === payload.id
    );
  if (isNotificationAlreadySaved) {
    return existingNotificationsForCurrentAccount;
  }

  const updateNotificationsForCurrentAccount = [
    ...existingNotificationsForCurrentAccount.notifications,
    newNotification,
  ];
  LocalStorage.setJSONValue("notifications", {
    ...existingNotifications,
    [accountId]: {
      notifications: updateNotificationsForCurrentAccount,
      areNewNotificationsAvailable: !isMenuOpen,
    },
  });
  return {
    notifications: updateNotificationsForCurrentAccount,
    areNewNotificationsAvailable: !isMenuOpen,
  };
};

export const markAllNotificationAsReadInStorage = (accountId: string): void => {
  const existingNotifications =
    LocalStorage.getJSONValue("notifications") || {};

  const existingNotificationsForCurrentAccount = (existingNotifications[
    accountId
  ] as AccountNotifications) || {
    notifications: [],
    areNewNotificationsAvailable: false,
  };

  const updatedNotificationsForCurrentAccount: AccountNotifications = {
    notifications: existingNotificationsForCurrentAccount.notifications.map(
      (notification) => ({ ...notification, isRead: true })
    ),
    areNewNotificationsAvailable: false,
  };

  LocalStorage.setJSONValue("notifications", {
    ...existingNotifications,
    [accountId]: updatedNotificationsForCurrentAccount,
  });
};

export const markNotificationsAsReadInStorage = (
  accountId: string,
  notificationIdsToMarkRead: string[]
): Notification[] => {
  const existingNotifications =
    LocalStorage.getJSONValue("notifications") || {};

  const existingNotificationsForCurrentAccount = (existingNotifications[
    accountId
  ] as AccountNotifications) || {
    notifications: [],
    areNewNotificationsAvailable: false,
  };

  const updatedNotifications =
    existingNotificationsForCurrentAccount.notifications.map((notification) =>
      notificationIdsToMarkRead.includes(notification.payload.id)
        ? { ...notification, isRead: true }
        : notification
    );
  const containsUnreadNotifications = updatedNotifications.some(
    (notification) => !notification.isRead
  );
  const updatedNotificationsForCurrentAccount: AccountNotifications = {
    notifications: updatedNotifications,
    areNewNotificationsAvailable: containsUnreadNotifications,
  };

  LocalStorage.setJSONValue("notifications", {
    ...existingNotifications,
    [accountId]: updatedNotificationsForCurrentAccount,
  });

  return updatedNotificationsForCurrentAccount.notifications;
};

export const expireNotificationInStorage = (
  accountId: string,
  notificationId: number
): Notification[] => {
  const existingNotifications =
    LocalStorage.getJSONValue("notifications") || {};
  const existingNotificationsForCurrentAccount = (existingNotifications[
    accountId
  ] as AccountNotifications) || {
    notifications: [],
    areNewNotificationsAvailable: false,
  };

  const updatedNotifications =
    existingNotificationsForCurrentAccount.notifications.map((notification) => {
      const isExpired =
        notification.isExpired ||
        new Date(notification.createdAt).getTime() === notificationId;
      return { ...notification, isExpired };
    });

  LocalStorage.setJSONValue("notifications", {
    ...existingNotifications,
    [accountId]: {
      ...existingNotificationsForCurrentAccount,
      notifications: updatedNotifications,
    },
  });

  return updatedNotifications;
};

export const deleteNotification = (
  accountId: string,
  ...notificationIdToDelete: number[]
): Notification[] => {
  const existingNotifications =
    LocalStorage.getJSONValue("notifications") || {};
  const existingNotificationsForCurrentAccount = (existingNotifications[
    accountId
  ] as AccountNotifications) || {
    notifications: [],
    areNewNotificationsAvailable: false,
  };

  const updatedNotifications =
    existingNotificationsForCurrentAccount.notifications.filter(
      (notification) =>
        !notificationIdToDelete.includes(
          new Date(notification.createdAt).getTime()
        )
    );

  LocalStorage.setJSONValue("notifications", {
    ...existingNotifications,
    [accountId]: {
      ...existingNotificationsForCurrentAccount,
      notifications: updatedNotifications,
    },
  });

  return updatedNotifications;
};
